import { Icon } from '@iconify/react';

import type {
  CheckoutResponse,
  CheckoutSessionResponse,
  CheckoutStatus,
  CheckoutSessionStatus,
} from '../api/checkout';
import {
  CHECKOUT_SESSION_STATUS_MESSAGES,
  isCheckoutTerminalStatus,
} from '../api/checkout';
import { networkDisplayName } from '../utils/networkLabels';
import { sessionPayDisplay } from '../utils/sessionDisplay';
import { paymentFailureCopy } from '../utils/paymentFailureCopy';
import OrderSummaryRail from './OrderSummaryRail';

export interface OutcomeViewProps {
  checkout: CheckoutResponse;
  session: CheckoutSessionResponse | null;
  /** When checkout is terminal before payment; session may be null. */
  checkoutOnly?: boolean;
  /** True when `expiresAt` is in the past (link no longer usable). */
  linkExpired?: boolean;
}

export default function OutcomeView({
  checkout,
  session,
  checkoutOnly = false,
  linkExpired = false,
}: OutcomeViewProps) {
  // A payment that ended at the provider drives BOTH the session and the parent
  // checkout to EXPIRED, so either branch below can be the one that renders.
  // The key comes from the server's own mapping; the UI never re-derives it.
  const failureCopy = paymentFailureCopy(session?.swap?.failureCopyKey);

  if (linkExpired) {
    return <SimpleCenter title="This payment link has expired" subtitle="No new payments can be made on this link." icon="mdi:clock-alert-outline" color="error.main" />;
  }

  if (checkoutOnly || isCheckoutTerminalStatus(checkout.status)) {
    switch (checkout.status) {
      case 'PAID':
        return <PaidOutcome checkout={checkout} session={session} />;
      case 'OVERPAID':
        return <PaidOutcome checkout={checkout} session={session} overpaid />;
      case 'UNDERPAID_REFUNDED':
        return <RefundLikeState title="Partial payment returned" subtitle="A refund has been sent to your refund address." session={session} />;
      case 'EXPIRED':
        return failureCopy ? (
          <SimpleCenter title={failureCopy.title} subtitle={failureCopy.body} icon="mdi:alert-circle-outline" color="error.main" />
        ) : (
          <SimpleCenter title="Session expired" subtitle="The payment window has passed. Start a new session if the link is still valid." icon="mdi:clock-outline" color="error.main" />
        );
      case 'CANCELED':
        return <SimpleCenter title="Order canceled" subtitle="This checkout was canceled by the merchant." icon="mdi:close-circle-outline" color="text.secondary" />;
      case 'FLAGGED_PENDING_REVIEW':
      case 'FLAGGED_PLATFORM_HELD':
      case 'FLAGGED_AUTO_RETURNED':
        return <RiskState checkoutStatus={checkout.status} />;
      default:
        break;
    }
  }

  if (!session) {
    return (
      <div className="ps-page">
        <div className="ps-shell ps-center">
          <div>
            <div style={{ fontSize: 20, fontWeight: 700 }}>Unable to load payment status.</div>
          </div>
        </div>
      </div>
    );
  }

  const st = session.status as CheckoutSessionStatus;

  switch (st) {
    case 'PAID':
      return <PaidOutcome checkout={checkout} session={session} />;
    case 'OVERPAID':
      return <PaidOutcome checkout={checkout} session={session} overpaid />;
    case 'EXPIRED':
      return failureCopy ? (
        <SimpleCenter title={failureCopy.title} subtitle={failureCopy.body} icon="mdi:alert-circle-outline" color="error.main" />
      ) : (
        <SimpleCenter title="Session expired" subtitle="No funds were sent, or the session timed out. You can start again if the link is still valid." icon="mdi:clock-outline" color="error.main" />
      );
    case 'REFUNDED':
      return <RefundLikeState title="Refund complete" subtitle="Your refund is confirmed on-chain and should now be in your wallet." session={session} />;
    case 'REFUNDING':
      return <RefundLikeState title="Refund in progress" subtitle={CHECKOUT_SESSION_STATUS_MESSAGES.REFUNDING} session={session} pending />;
    case 'REFUND_FAILED':
      return <RefundLikeState title="Refund failed" subtitle={CHECKOUT_SESSION_STATUS_MESSAGES.REFUND_FAILED} session={session} failed />;
    case 'FLAGGED_PENDING_REVIEW':
    case 'FLAGGED_PLATFORM_HELD':
      return (
        <RiskState checkoutStatus={st} />
      );
    case 'AWAITING_PAYMENT':
    case 'PARTIAL':
    case 'CONFIRMING':
    case 'RISK_SCAN_PENDING':
      return (
        <div className="ps-page">
          <div className="ps-shell ps-center">
            <div className="ps-alert info" style={{ width: '100%', maxWidth: 380 }}>
              {CHECKOUT_SESSION_STATUS_MESSAGES[st]}
            </div>
          </div>
        </div>
      );
    default:
      return (
        <div className="ps-page">
          <div className="ps-shell ps-center">
            <div className="ps-subtitle">Unable to load payment status.</div>
          </div>
        </div>
      );
  }
}

function PaidOutcome({
  checkout,
  session,
  overpaid = false,
}: {
  checkout: CheckoutResponse;
  session: CheckoutSessionResponse | null;
  overpaid?: boolean;
}) {
  const usd = Number(checkout.priceUsdSnapshot).toFixed(2);

  // A swap-funded session over-delivers BY DESIGN: the quote asks for
  // `amountRequired` plus CHECKOUT_SWAP_DELIVERY_BUFFER_BPS, and
  // `evaluatePayment`'s tolerance is one-sided, so any excess reads as
  // OVERPAID. The payer sent exactly what we asked them for, in a token that
  // is not the one recorded here — telling them they overpaid, in USDC, is
  // both wrong from where they stand and a leak of the mechanism. The
  // merchant's ledger keeps the real figures; this screen is not the ledger.
  const pay = session ? sessionPayDisplay(session) : null;

  // `pay !== null` is load-bearing, not a null-guard. On a reload the outcome
  // screen has the checkout but no session, so it cannot tell a swap from a
  // native payment AND has no amounts to show. Claiming an overpayment there
  // is an assertion we cannot support and, for a swap, is simply false.
  // "Payment confirmed" is the honest message when the detail is absent.
  const showOverpaid = overpaid && pay !== null && !pay.isSwap;

  const amountPaid = pay?.isSwap
    ? pay.amount
    : session
      ? session.amountReceived || session.amountRequired
      : null;
  const paidSymbol = pay?.symbol ?? session?.token ?? '';
  const overage =
    session && showOverpaid
      ? Math.max(0, Number(session.amountReceived) - Number(session.amountRequired)).toFixed(8)
      : null;

  return (
    <div className="ps-page">
      <div className="ps-shell">
        <div className="ps-body">
          <OrderSummaryRail checkout={checkout} />
          <section className="ps-main" style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
            <div className="ps-icon-circle" style={{ background: '#dcfce7', margin: 0 }}>
              <Icon icon="mdi:check" width={40} height={40} color="#16a34a" />
            </div>
            <h1 style={{ margin: '8px 0 0', fontSize: 28 }}>Payment confirmed</h1>
            <p className="ps-subtitle" style={{ maxWidth: 360 }}>
              {showOverpaid
                ? 'Your order is confirmed. We detected a slight overpayment.'
                : 'Your order has been paid in full. The merchant has been notified and will process your order shortly.'}
            </p>

            {showOverpaid && overage ? (
              <div className="ps-alert warning" style={{ textAlign: 'left', width: '100%', maxWidth: 460 }}>
                <strong>Overpayment of {overage} {session?.token} detected</strong>
                <div>
                  The excess amount will be handled by the merchant per their refund policy. No action
                  needed on your part.
                </div>
              </div>
            ) : null}

            {session ? (
              <div className="ps-kv-card" style={{ textAlign: 'left', width: '100%', maxWidth: 460 }}>
                {showOverpaid ? (
                  <>
                    <div className="ps-kv-row">
                      <span className="ps-muted">Required</span>
                      <span className="ps-kv-value">{session.amountRequired} {session.token}</span>
                    </div>
                    <div className="ps-kv-row">
                      <span className="ps-muted">Received</span>
                      <span className="ps-kv-value">{session.amountReceived} {session.token}</span>
                    </div>
                    <div className="ps-kv-row">
                      <span className="ps-muted">Overpayment</span>
                      <span className="ps-kv-value">{overage} {session.token}</span>
                    </div>
                    <div className="ps-kv-row">
                      <span className="ps-muted">Status</span>
                      <span className="ps-kv-value">Handled by merchant</span>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="ps-kv-row">
                      <span className="ps-muted">Amount paid</span>
                      <span className="ps-kv-value">{amountPaid === null ? '—' : `${amountPaid} ${paidSymbol}`}</span>
                    </div>
                    <div className="ps-kv-row">
                      <span className="ps-muted">USD value</span>
                      <span className="ps-kv-value">${usd}</span>
                    </div>
                    <div className="ps-kv-row">
                      <span className="ps-muted">Network</span>
                      <span className="ps-kv-value">{pay?.chainLabel ?? networkDisplayName(session.network)}</span>
                    </div>
                    <div className="ps-kv-row">
                      <span className="ps-muted">Confirmed at</span>
                      {/* ponytail: static label per design; wire to session.updatedAt if a real timestamp is needed */}
                      <span className="ps-kv-value">Just now</span>
                    </div>
                  </>
                )}
              </div>
            ) : null}

            {checkout.successUrl ? (
              <a
                href={checkout.successUrl}
                className="ps-button primary"
                style={{ display: 'inline-block', textDecoration: 'none', marginTop: 4, width: 220 }}
              >
                Continue
              </a>
            ) : null}
          </section>
        </div>
      </div>
    </div>
  );
}

function SimpleCenter({
  title,
  subtitle,
  icon,
  color,
}: {
  title: string;
  subtitle: string;
  icon: string;
  color: string;
}) {
  return (
    <div className="ps-page">
      <div className="ps-shell ps-center">
        <div style={{ width: '100%', maxWidth: 420 }}>
          <div className="ps-icon-circle" style={{ background: '#f3f4f6' }}>
            <Icon icon={icon} width={56} height={56} color={color} />
          </div>
          <h2 style={{ margin: 0, fontSize: 22 }}>{title}</h2>
          <p className="ps-subtitle">{subtitle}</p>
        </div>
      </div>
    </div>
  );
}

function RiskState({ checkoutStatus }: { checkoutStatus: CheckoutStatus | CheckoutSessionStatus }) {
  let title = 'Under review';
  let body = 'Your payment is under review. You will be contacted if further action is required.';
  let severity: 'warning' | 'error' = 'warning';
  if (checkoutStatus === 'FLAGGED_PLATFORM_HELD') {
    title = 'Payment held for compliance';
    body = 'This payment is held by our compliance team. The merchant cannot release funds at this time.';
  }
  if (checkoutStatus === 'FLAGGED_AUTO_RETURNED') {
    title = 'Payment rejected and returned';
    body = 'This payment was auto-returned by compliance policy. Funds usually arrive within a few minutes.';
    severity = 'error';
  }
  return (
    <div className="ps-page">
      <div className="ps-shell ps-center">
        <div style={{ width: '100%', maxWidth: 480 }}>
          <div className="ps-icon-circle" style={{ background: '#f5f3ff' }}>
            <Icon icon="mdi:shield-outline" width={32} height={32} color="#7c3aed" />
          </div>
          <h2 style={{ margin: 0, fontSize: 22 }}>{title}</h2>
          <div className={`ps-alert ${severity}`} style={{ marginTop: 14 }}>
            {body}
          </div>
        </div>
      </div>
    </div>
  );
}

function RefundLikeState({
  title,
  subtitle,
  session,
  pending = false,
  failed = false,
}: {
  title: string;
  subtitle: string;
  session: CheckoutSessionResponse | null;
  pending?: boolean;
  failed?: boolean;
}) {
  return (
    <div className="ps-page">
      <div className="ps-shell ps-center">
        <div style={{ width: '100%', maxWidth: 520 }}>
          <Icon
            icon={failed ? 'mdi:close-circle' : pending ? 'mdi:autorenew' : 'mdi:check-circle'}
            width={56}
            height={56}
            color={failed ? '#dc2626' : pending ? '#2563eb' : '#16a34a'}
          />
          <h2 style={{ margin: '8px 0 0', fontSize: 22 }}>{title}</h2>
          <p className="ps-subtitle">{subtitle}</p>
          {session ? (
            <div className="ps-card" style={{ maxWidth: 420, margin: '0 auto', textAlign: 'left' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Row label="Amount" value={`${session.amountReceived} ${session.token}`} />
                <Row label="Refund address" value={session.refundAddress} monospace />
                <Row label="Session" value={session.id} monospace />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, monospace = false }: { label: string; value: string; monospace?: boolean }) {
  return (
    <div className="ps-row" style={{ marginBottom: 0 }}>
      <span style={{ fontSize: 13, color: '#6b7280' }}>
        {label}
      </span>
      <span style={{ fontSize: 13, fontFamily: monospace ? 'monospace' : 'inherit', textAlign: 'right' }}>
        {value}
      </span>
    </div>
  );
}
